import { palette, semantic, status, surface, space, type } from './theme/tokens';

export const COLOURS = /** @type {const} */ ({
  default: semantic.default.name,
  cardDecoration: semantic.cardDecoration.name,
  tableHeader: 'neutral',

  good: semantic.good.name,
  error: semantic.error.name,
  warning: semantic.warning.name,

  unknown: 'slate',
  other: 'zinc',
  neutral: 'neutral',

  heating: 'orange',
  purging: 'indigo',
  standby: 'teal',

  apiAccessOff: 'stone',
  apiAccessData: 'lime',
  apiAccessControl: 'emerald',

  modelThrealux: 'cyan',
  modelOASIS: 'amber',
  modelAquatek: 'green',
  modelAqualight: 'violet',
  modelReclaim: 'stone',
  modelPooltek: 'lime',
  modelHenden: 'pink',
  modelSummertime: 'emerald',

  modelList: ['red', 'cyan', 'amber', 'green', 'violet', 'stone', 'lime', 'pink', 'emerald'],

  primary: semantic.primary.hex,

  buttonTextSelected: '#fff',
  buttonTextUnselected: '#888',

  border: semantic.border.hex,
  goodHex: semantic.good.hex,
  errorHex: semantic.error.hex,
  warningHex: semantic.warning.hex,
  cardDecorationHex: semantic.cardDecoration.hex,
  titleHex: semantic.title.hex,
  defaultHex: semantic.default.hex,

  faultsList: [
    'red',
    'orange',
    'amber',
    'yellow',
    'indigo',
    'violet',
    'purple',
    'fuschia',
    'lime',
    'pink',
    'emerald',
    'neutral',
    'amber',
    'sky',
    'red',
    'orange',
  ],

  connected: status.connected,
  connecting: status.connecting,
  disconnected: status.disconnected,
  waiting: status.waiting,
});

// Tailwind 500-shade hexes for every colour NAME used across COLOURS, params.js,
// and the per-device *Color fields. Tremor accepted these bare names; rsuite Tag,
// Recharts, and inline styles need real CSS colours, so resolveColor() maps them.
const TAILWIND_500 = Object.fromEntries(Object.entries(palette).map(([name, shades]) => [name, shades[500]]));

/**
 * Resolve a colour token to a real CSS colour.
 * Accepts a hex value as-is, a Tailwind colour name (e.g. "green"), and
 * normalises the "fuschia" misspelling in COLOURS.faultsList to "fuchsia".
 * Falls back to slate-500 for unknown names.
 * @param {string | undefined} colour
 * @returns {string}
 */
export const resolveColor = (colour) => {
  if (typeof colour !== 'string' || colour.length === 0) return TAILWIND_500.slate;
  if (colour.startsWith('#')) return colour;
  const name = colour === 'fuschia' ? 'fuchsia' : colour;
  return TAILWIND_500[name] || TAILWIND_500.slate;
};

// Tailwind 700-shade hexes — used for the darker text/border tone Tremor's
// own Callout pairs with a pale tinted background (its "darkText"/"darkBorder"
// theme tiers, both 700 in @tremor/react's colorPalette).
const TAILWIND_700 = Object.fromEntries(Object.entries(palette).map(([name, shades]) => [name, shades[700]]));

/**
 * Darker-tier counterpart to resolveColor(), for pairing with a pale tinted
 * background (see resolveColorTint()) the way Tremor's Callout does.
 * @param {string | undefined} colour
 * @returns {string}
 */
export const resolveColorDark = (colour) => {
  if (typeof colour !== 'string' || colour.length === 0) return TAILWIND_700.slate;
  if (colour.startsWith('#')) return colour;
  const name = colour === 'fuschia' ? 'fuchsia' : colour;
  return TAILWIND_700[name] || TAILWIND_700.slate;
};

const hexToRgb = (hex) => {
  // Expand 3-digit shorthand (#rgb -> #rrggbb, each digit doubled) before
  // parsing — resolveColor() passes hex values through as-is, so a caller
  // using shorthand would otherwise be parsed against the wrong bit offsets.
  const full = hex.length === 4 ? `#${[...hex.slice(1)].map((c) => c + c).join('')}` : hex;
  const value = parseInt(full.slice(1), 16);
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
};

/**
 * A pale tinted background for a colour name — Tremor's Callout achieves this
 * with `bg-{color}-500 bg-opacity-10` (the 500-shade colour at 10% alpha over
 * the page background) rather than a lighter shade number; this reproduces
 * that exact mechanism so the visual result matches.
 * @param {string | undefined} colour
 * @param {number} [alpha]
 * @returns {string}
 */
export const resolveColorTint = (colour, alpha = 0.1) => {
  const hex = resolveColor(colour);
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Inline style reproducing @tremor/react v3.16.2's <Badge> appearance — the
 * look `main` rendered before the rsuite pivot, which the migration replaced
 * with a solid-fill/white-text pill. Tremor's real recipe (read from its
 * compiled source): background = colour-500 at 10% opacity, ring = colour-500
 * at 20% opacity, text = colour-600. This palette only defines the 500/700
 * tiers, so the darker 700 stands in for Tremor's 600 text (marginally darker,
 * still faithful); the 10%/20% opacities match Tremor's bg-opacity-10 /
 * ring-opacity-20 exactly. Returns the full pill style (layout + colour) so
 * every badge site stays visually identical.
 * @param {string | undefined} colour
 */
export const tremorBadgeStyle = (colour) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  whiteSpace: 'nowrap',
  padding: '2px 8px',
  borderRadius: 4,
  fontSize: '0.85em',
  lineHeight: 1.4,
  backgroundColor: resolveColorTint(colour),
  color: resolveColorDark(colour),
  boxShadow: `inset 0 0 0 1px ${resolveColorTint(colour, 0.2)}`,
});

export const csvLinkStyle = {
  backgroundColor: COLOURS.primary,
  borderRadius: space.radius.card,
  color: COLOURS.buttonTextSelected,
  fontSize: type.size.sm,
  fontWeight: type.weight.medium,
  padding: space.pad.button,
};

export const buttonGroupStyle = {
  backgroundColor: surface.cardBg,
  borderColor: COLOURS.border,
  borderRadius: space.radius.group,
  borderStyle: 'solid',
  borderWidth: '1px',
};

export const defaultCardStyle = {
  backgroundColor: surface.cardBg,
  borderColor: COLOURS.border,
  borderRadius: space.radius.card,
  borderStyle: 'solid',
  borderWidth: '1px',
  padding: space.pad.card,
};

export const headerCardStyle = {
  backgroundColor: surface.cardBg,
  borderColor: COLOURS.border,
  borderRadius: space.radius.card,
  borderStyle: 'solid',
  borderWidth: '1px',
  padding: space.pad.tight,
};

export const progressCardStyle = {
  backgroundColor: surface.cardBg,
  borderColor: COLOURS.border,
  borderRadius: space.radius.card,
  borderStyle: 'solid',
  borderWidth: '1px',
  padding: space.pad.card,
  marginTop: space.gap.sm,
};

export const gridCardStyle = {
  backgroundColor: surface.cardBg,
  borderColor: COLOURS.border,
  borderRadius: space.radius.card,
  borderStyle: 'solid',
  borderWidth: '1px',
  padding: space.pad.grid,
  marginTop: space.gap.md,
};

export const fixedHeaderCardStyle = {
  backgroundColor: surface.subtle,
  padding: space.pad.tight,
};

export const unfixedHeaderCardStyle = {
  backgroundColor: surface.white,
  padding: space.pad.tight,
};

export const headingStyle = {
  zIndex: 1000,
  // Higher z-index value
  padding: space.pad.heading,
  borderRadius: space.radius.sm,
  margin: '0',
  textShadow: `2px 2px 4px ${surface.cardBg}`,
};
